/**
 * Note Sharing Service
 * Client-side functions to interact with sharing Cloud Functions
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase-config.js';

/**
 * Create a share link for a note
 * @param {Object} note - The note to share
 * @param {Object} options - Sharing options
 * @returns {Promise<Object>} - { success, shareId, shareUrl, expiresAt }
 */
export async function createShareLink(note, options = {}) {
    const { isPublic = true, sharedWithEmails = [], expiresInDays = null } = options;

    // Extract plain text content for sharing
    const content = extractDisplayContent(note.content);
    const title = extractTitle(note.content) || note.title;

    const createShare = httpsCallable(functions, 'createShareLink');
    const result = await createShare({
        noteKey: note.key,
        title,
        content,
        domain: note.domain,
        isPublic,
        sharedWithEmails,
        expiresInDays
    });

    return result.data;
}

/**
 * Get a shared note by share ID
 * @param {string} shareId - The share ID
 * @returns {Promise<Object>} - Shared note data
 */
export async function getSharedNote(shareId) {
    const getShare = httpsCallable(functions, 'getSharedNote');
    const result = await getShare({ shareId });
    return result.data;
}

/**
 * Update share settings
 * @param {string} shareId - The share ID
 * @param {Object} updates - Settings to update
 * @returns {Promise<Object>}
 */
export async function updateShareSettings(shareId, updates) {
    const updateShare = httpsCallable(functions, 'updateShareSettings');
    const result = await updateShare({ shareId, ...updates });
    return result.data;
}

/**
 * Revoke a share link
 * @param {string} shareId - The share ID to revoke
 * @returns {Promise<Object>}
 */
export async function revokeShare(shareId) {
    const revoke = httpsCallable(functions, 'revokeShare');
    const result = await revoke({ shareId });
    return result.data;
}

/**
 * Get notes shared with the current user
 * @returns {Promise<Object>} - { notes: [...] }
 */
export async function getSharedWithMe() {
    const getShared = httpsCallable(functions, 'getSharedWithMe');
    const result = await getShared({});
    return result.data;
}

/**
 * Get all shares created by the current user
 * @returns {Promise<Object>} - { shares: [...] }
 */
export async function getMyShares() {
    const getShares = httpsCallable(functions, 'getMyShares');
    const result = await getShares({});
    return result.data;
}

/**
 * Sync share content when note is updated
 * @param {string} shareId - The share ID
 * @param {Object} note - Updated note data
 */
export async function syncShareContent(shareId, note) {
    const content = extractDisplayContent(note.content);
    const title = extractTitle(note.content) || note.title;

    return updateShareSettings(shareId, { content, title });
}

// Helper: Extract title from note content
function extractTitle(content) {
    if (!content) return '';

    try {
        if (typeof content === 'string') {
            const parsed = JSON.parse(content);
            if (parsed.version === 2 && parsed.content) {
                const text = extractTextFromTiptap(parsed.content);
                const firstLine = text.split('\n')[0].trim();
                return firstLine.substring(0, 50);
            }
        }
        return content.substring(0, 50);
    } catch {
        return content.substring(0, 50);
    }
}

// Helper: Extract display content from note
function extractDisplayContent(content) {
    if (!content) return '';

    try {
        if (typeof content === 'string') {
            const parsed = JSON.parse(content);
            if (parsed.version === 2 && parsed.content) {
                return convertTiptapToHTML(parsed.content);
            }
        }
        return content;
    } catch {
        return content;
    }
}

// Helper: Extract text from TipTap JSON
function extractTextFromTiptap(json) {
    if (!json) return '';

    const extractFromNode = (node) => {
        if (!node) return '';
        if (node.type === 'text') return node.text || '';
        if (node.content && Array.isArray(node.content)) {
            return node.content.map(extractFromNode).join('');
        }
        return '';
    };

    if (json.content && Array.isArray(json.content)) {
        return json.content.map(extractFromNode).join('\n');
    }

    return extractFromNode(json);
}

// Helper: Convert TipTap JSON to HTML for display
function convertTiptapToHTML(json) {
    if (!json) return '';

    const processNode = (node) => {
        if (!node) return '';

        if (node.type === 'text') {
            let text = escapeHtml(node.text || '');
            if (node.marks) {
                for (const mark of node.marks) {
                    if (mark.type === 'bold') text = `<strong>${text}</strong>`;
                    if (mark.type === 'italic') text = `<em>${text}</em>`;
                    if (mark.type === 'strike') text = `<del>${text}</del>`;
                    if (mark.type === 'code') text = `<code>${text}</code>`;
                    if (mark.type === 'link') text = `<a href="${escapeHtml(mark.attrs?.href || '')}">${text}</a>`;
                }
            }
            return text;
        }

        if (node.type === 'paragraph') {
            const content = node.content ? node.content.map(processNode).join('') : '';
            return `<p>${content}</p>`;
        }

        if (node.type === 'heading') {
            const level = node.attrs?.level || 1;
            const content = node.content ? node.content.map(processNode).join('') : '';
            return `<h${level}>${content}</h${level}>`;
        }

        if (node.type === 'bulletList') {
            const items = node.content ? node.content.map(processNode).join('') : '';
            return `<ul>${items}</ul>`;
        }

        if (node.type === 'orderedList') {
            const items = node.content ? node.content.map(processNode).join('') : '';
            return `<ol>${items}</ol>`;
        }

        if (node.type === 'listItem') {
            const content = node.content ? node.content.map(processNode).join('') : '';
            return `<li>${content}</li>`;
        }

        if (node.type === 'codeBlock') {
            const code = node.content ? node.content.map(n => n.text || '').join('') : '';
            return `<pre><code>${escapeHtml(code)}</code></pre>`;
        }

        if (node.type === 'blockquote') {
            const content = node.content ? node.content.map(processNode).join('') : '';
            return `<blockquote>${content}</blockquote>`;
        }

        if (node.content && Array.isArray(node.content)) {
            return node.content.map(processNode).join('');
        }

        return '';
    };

    if (json.content && Array.isArray(json.content)) {
        return json.content.map(processNode).join('');
    }

    return processNode(json);
}

function escapeHtml(text) {
    if (!text) return '';
    const str = String(text);
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}
